import { TabContext, TabList } from '@mui/lab';
import {
	Box,
	Tab,
	TextField,
	Typography,
	Grid,
	Tooltip,
	IconButton,
} from '@mui/material';

import React, {
	ChangeEvent,
	useCallback,
	useState,
	useRef,
	useEffect,
} from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { ReactComponent as BotAvatar } from 'common/assets/images/bot-avatar.svg';
import { ReactComponent as Right } from 'common/assets/images/check-mark.svg';
import { ReactComponent as Cancel } from 'common/assets/images/close.svg';
import { ReactComponent as Divider } from 'common/assets/images/Divider.svg';
import { ReactComponent as DrawerClose } from 'common/assets/images/drawer-close.svg';
import { ReactComponent as Pencil } from 'common/assets/images/edit-pencil.svg';
import { ReactComponent as Expand } from 'common/assets/images/expand-zoom.svg';
import { ReactComponent as PaperClip } from 'common/assets/images/paperclip.svg';
import { ReactComponent as Plus } from 'common/assets/images/Plus.svg';
import { ReactComponent as Collapse } from 'common/assets/images/risk-gpt-collapse.svg';
import { ReactComponent as SendDisable } from 'common/assets/images/risk-gpt-send-disabled.svg';
import { ReactComponent as Send } from 'common/assets/images/risk-gpt-send-enabled.svg';
import ButtonComp from 'common/components/Button/ButtonComp';
import DeleteFileModal from 'common/components/FileUpload/DeleteFileModal';
import FileUpload from 'common/components/FileUpload/FileUpload';
import DotsLoader from 'common/components/Loader/DotsLoader';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import {
	ACCEPTED_CHAT_FILE_TYPES,
	BAD_REQUEST_MSG,
	DELETE_FILE_SUCCESS_MESSAGE,
	TOAST_SEVERITY,
	maxFileAllowedForActiveChatSession,
} from 'common/constants';
import { AI_SIMULATION_GENAI_DISCLAIMER } from 'common/constants/AISimulationConstants';
import {
	CHAT_USER_OPTIONS,
	TAB_VALUES,
	TIME_STAMP_OPTIONS,
} from 'common/constants/AskRiskLinkConstants';
import { FeatureFlagsKeysEnum } from 'common/constants/FeatureFlags';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { RootState } from 'common/store/AppStore';
import {
	setRiskGptDrawer,
	setRiskGptShouldExpand,
	setRiskGptSessionDetails,
	setRiskGptKnowledgeBase,
	setActiveChatSessionUploadedFiles,
	setActiveChatSearchTextSelected,
	setReUploadHistory,
	setActiveChatSessionUploadingFiles,
	clearUploadedDocumentProgress,
	setUploadedHistory,
	setRiskGptAllowDrag,
	setFromHistory,
} from 'common/store/reducer/commonReducer';
import { toSentenceCase } from 'common/utils/utilFunctions';

import { useFeatureFlag } from 'hooks/useFeatureFlags';

import {
	IFileInfo,
	IUploadingFile,
	IUploadingFileStatus,
} from 'features/Reporting/api/type-def';

import {
	createChatSession,
	sendMessageToGenAPI,
	getChatName,
	updateChatName,
	getGPTMessagesResponse,
	getSessionData,
	deleteChatSessionDocument,
} from './api';
import {
	IDeleteDocumentRequestParams,
	IGPTMessageProps,
	IUploadResponse,
} from './api/type-def';
import ChatHistory from './ChatHistory/ChatHistory';
import ChatRecords from './ChatRecords/ChatRecords';
import KnowledgeBase from './KnowledgeBase/KnowledgeBase';
import {
	currentTimeStamp,
	cursorSetterHandler,
	formatMessageFromApi,
	getUserName,
	getMessageWithReUpload,
	OPEN_RISK_GPT,
	getRefreshedReUploadHistory,
	fileUploadStartHandler,
	getMessageWithUploadedFiles,
	filterUploadFinishedFiles,
	filterDocsFromUploadedMessages,
} from './OpenRiskGPTUtil';
import PromptLibrary from './PromptLibrary/PromptLibrary';
import RiskGptMessageView from './RiskGptMessage/RiskGptMessageView';
import UploadDocumentList from './UploadDocument/UploadDocumentList';

import styles from './OpenRiskGPT.module.scss';

const { ERROR, SUCCESS } = TOAST_SEVERITY;
const { DISCLAIMER } = AI_SIMULATION_GENAI_DISCLAIMER;

const {
	CHAT_NAME,
	CHAT_SESSION_ERROR_MESSAGE,
	CHAT_SESSION_UNABLE_RETRIEVE_MESSAGE,
	ASK_RISK_LINK,
	RESPONSE_FAILURE_MESSAGE,
	CHAT_TITLE_FAILURE_MESSAGE,
	CHAT_NAME_UPDATE_SUCCESS_MESSAGE,
	CHAT_NAME_UPDATE_FAILURE_MESSAGE,
	CHAT_HISTORY_RETRIVE_FAILURE_MESSAGE,
	WELCOME_MESSAGE,
	NETWORK_ISSUE_MESSAGE,
	NEW_CHAT,
} = OPEN_RISK_GPT;
const { OK } = HTTP_STATUS_CODES;
const { CHAT, PROMPT_LIBRARY, CHAT_HISTORY, RECORDS } = TAB_VALUES;
const { USER, BOT } = CHAT_USER_OPTIONS;
const { DATE, TIME } = TIME_STAMP_OPTIONS;

const OpenRiskGPT = () => {
	const {
		inputPadding,
		searchDisabled,
		selectedTabColor,
		closeIconColor,
		textFieldColor,
		paperClipIconColor,
	} = styles;

	const dispatch = useDispatch();
	const { REACT_APP_GEN_AI_SERVICE_URL } = process.env;
	const { IS_ASK_RISK_LINK_UPLOAD_FILE_ENABLED } = FeatureFlagsKeysEnum;

	const riskGptSessionDetails = useSelector(
		(state: RootState) => state.common.riskGptSessionDetails,
	);

	const riskGptDrawerState = useSelector(
		(state: RootState) => state.common.riskGptDrawer,
	);

	const loggedInUser = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);

	const riskGptShouldExpand = useSelector(
		(state: RootState) => state.common.riskGptShouldExpand,
	);
	const riskGptAllowDrag = useSelector(
		(state: RootState) => state.common.riskGptAllowDrag,
	);

	const riskGptKnowledgeBase = useSelector(
		(state: RootState) => state.common.riskGptKnowledgeBase,
	);
	const reUploadHistory = useSelector(
		(state: RootState) => state.common.reUploadHistory,
	);
	const activeChatSessionUploadingFiles = useSelector(
		(state: RootState) => state.common.activeChatSessionUploadingFiles,
	);
	const activeChatSessionUploadedFiles = useSelector(
		(state: RootState) => state.common.activeChatSessionUploadedFiles,
	);
	const activeChatSearchTextSelected = useSelector(
		(state: RootState) => state.common.activeChatSearchTextSelected,
	);
	const listRef = useRef<null | HTMLDivElement>(null);
	const [searchVal, setSearchVal] = useState<string>('');
	const [promptRole, setPromptRole] = useState<string>('');

	const [chatEditSearchVal, setChatEditSearchVal] = useState<string>(
		riskGptSessionDetails.chatName,
	);
	const [messages, setMessages] = useState<IGPTMessageProps[]>(
		riskGptSessionDetails.messages,
	);
	const [loading, setIsLoading] = useState<boolean>(false);
	const [chatTitle, setChatTitle] = useState<string>(
		riskGptSessionDetails.chatName,
	);
	const [editTitleClicked, setEditTitleClicked] = useState<boolean>(false);
	const [value, setValue] = useState<string>(CHAT);
	const [sessionId, setSessionId] = useState<string>(
		riskGptSessionDetails.sessionId,
	);
	const [toastMessage, setToastMessage] = useState<string>('');
	const [isToastOpen, setIsToastOpen] = useState<boolean>(false);
	const [severity, setSeverity] = useState<string>('');
	const [cursorPosition, setCursorPosition] = useState<any>();
	const [isUploadSelected, setIsUploadSelected] = useState<boolean>(false);
	const [uploadUrl, setUploadUrl] = useState<string>('');
	const [isFileDeleteSelected, setFileDeletionStatus] =
		useState<boolean>(false);
	const [deleteFileInfo, setDeleteFileInfo] = useState<any>();
	const [component, setComponent] = useState<string>('');
	const [deleteBtnLoading, setDeleteBtnLoading] = useState<boolean>(false);
	const isAskRiskLinkUploadFileEnabled = useFeatureFlag(
		IS_ASK_RISK_LINK_UPLOAD_FILE_ENABLED,
	);

	const riskGPTKnowledgeOptions = useSelector(
		(state: RootState) => state.common.riskGptKnowledgeBaseOptions,
	);

	const drawerCloseHandler = useCallback(() => {
		dispatch(
			setRiskGptSessionDetails({
				...riskGptSessionDetails,
				messages,
				sessionId,
				chatName: chatTitle,
			}),
		);
		dispatch(setRiskGptShouldExpand(false));
		dispatch(setRiskGptDrawer(!riskGptDrawerState));
	}, [riskGptDrawerState, messages, searchVal, sessionId, chatTitle]);

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		event.stopPropagation();
		if (newValue === RECORDS) {
			if (!riskGptShouldExpand) {
				dispatch(setRiskGptShouldExpand(true));
			}
			if (riskGptAllowDrag) {
				dispatch(setRiskGptAllowDrag(false));
			}
		} else if (!riskGptAllowDrag) {
			dispatch(setRiskGptAllowDrag(true));
		}
		setValue(newValue);
	};

	const handleToastChange = (message: string, severity: string) => {
		setIsToastOpen(true);
		setToastMessage(message);
		setSeverity(severity);
	};

	const onPromptSelect = (message: string, role: string) => {
		setSearchVal(message);
		setPromptRole(role);
	};

	const createChatSessionAPI = async (role?: string, prompt?: string) => {
		const searchTerm = prompt?.length ? prompt : searchVal;
		if (sessionId) {
			fetchRiskGPTResponse(sessionId, searchTerm, role);
		} else {
			setIsLoading(true);
			const baseKB = riskGptKnowledgeBase;
			const response: any = await createChatSession(CHAT_NAME, baseKB);
			if (response?.statusCode === OK) {
				setSessionId(response.responseBody.id);
				fetchRiskGPTResponse(response.responseBody.id, searchTerm, role);
			} else {
				handleToastChange(CHAT_SESSION_ERROR_MESSAGE, ERROR);
			}
		}
		if (promptRole) {
			setPromptRole('');
		}
	};

	const getFilteredFilesInfo = (data: any) =>
		data?.length > 0 ? filterUploadFinishedFiles(data) : [];
	const getSessionDetails = async (
		id: string,
		showChatDetails?: boolean,
		isUploadSuccess?: boolean,
		shouldRefreshFileHistory?: boolean,
	) => {
		const response: any = await getSessionData(id);
		if (response?.statusCode === OK) {
			if (!showChatDetails) {
				setChatTitle(response.responseBody.name);
				setChatEditSearchVal(response.responseBody.name);
			}
			const docInfoFromApi = response.responseBody?.documents;
			const finalDocs = getFilteredFilesInfo(docInfoFromApi);

			const uploadedFile = finalDocs;
			dispatch(setActiveChatSessionUploadedFiles(uploadedFile));
			if (shouldRefreshFileHistory) dispatch(setUploadedHistory(uploadedFile));
			if (uploadedFile.length === 0) {
				// delete upload history from message
				setMessages((prev) =>
					prev.filter(
						(message: IGPTMessageProps) =>
							Boolean(message.isUploadStatus) === false,
					),
				);
			}
			/**
			 * @description to not fresh the reUploadHistory
			 * if can fetch the latest uploadedFile from api,
			 * no need to fresh reUploadHistory with these senario, because of reUploadHistory remove
			 * 1. upload success
			 * 2. session id changed
			 */
			if (
				sessionId === id &&
				reUploadHistory &&
				reUploadHistory?.reuploadSuccessFiles.length === 0 &&
				!isUploadSuccess
			) {
				const { failedFileArr, newReUploadHistory } =
					getRefreshedReUploadHistory(uploadedFile, reUploadHistory);
				dispatch(
					setReUploadHistory(
						failedFileArr.length > 0 ? newReUploadHistory : null,
					),
				);
				dispatch(setActiveChatSessionUploadingFiles(failedFileArr));
			}
			setIsUploadSelected(false);
		} else {
			handleToastChange(CHAT_SESSION_UNABLE_RETRIEVE_MESSAGE, ERROR);
		}
		setDeleteBtnLoading(false);
		setFileDeletionStatus(false);
		return response?.statusCode === OK;
	};

	const handleMessageSend = () => {
		setSearchVal('');
		const message: IGPTMessageProps = {
			type: USER,
			name: loggedInUser
				? getUserName(loggedInUser?.firstName, loggedInUser?.lastName)
				: '',
			time: currentTimeStamp(TIME),
			date: currentTimeStamp(DATE),
			message: searchVal,
			component,
		};
		setMessages((prev) => [...prev, message]);
		createChatSessionAPI(promptRole);
		listRef?.current?.scrollIntoView({ behavior: 'smooth' });
		if (value === PROMPT_LIBRARY) {
			setValue(CHAT);
		}
	};

	const shouldExpandRiskGptWindow = () => {
		dispatch(setRiskGptShouldExpand(!riskGptShouldExpand));
	};

	const copyToClipBoard = async (query: string) => {
		await navigator.clipboard.writeText(query);
	};

	const regenerateLastQueryResponse = (previousPrompt: IGPTMessageProps) => {
		const previousMessage = previousPrompt?.prompt || '';
		const lastUserObject: IGPTMessageProps = {
			type: USER,
			name: loggedInUser
				? getUserName(loggedInUser?.firstName, loggedInUser?.lastName)
				: '',
			time: currentTimeStamp(TIME),
			date: currentTimeStamp(DATE),
			message: previousMessage,
			component,
		};
		setMessages((prev) => [...prev, lastUserObject]);
		createChatSessionAPI(previousPrompt.promptRole, previousMessage);
	};

	const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
		setCursorPosition(e.target.selectionStart);
		dispatch(setActiveChatSearchTextSelected(true));
		setSearchVal(e.target.value);
	};

	const fetchRiskGPTResponse = async (
		id: string,
		prompt: string,
		role?: string,
	) => {
		setIsLoading(true);
		const responseFromApi: any = await sendMessageToGenAPI(prompt, id, role);

		if (responseFromApi.responseBody && responseFromApi.statusCode === OK) {
			const responseObject: IGPTMessageProps = {
				type: BOT,
				name: ASK_RISK_LINK,
				time: currentTimeStamp(TIME),
				date: currentTimeStamp(DATE),
				messageId: responseFromApi.responseBody?.id,
				uniqueId: responseFromApi.responseBody?.id,
				message: responseFromApi?.responseBody?.detail?.response,
				prompt: responseFromApi?.responseBody?.detail?.prompt,
				component: responseFromApi?.responseBody?.component,
				sources: responseFromApi?.responseBody?.detail?.sources,
				promptRole: responseFromApi?.responseBody?.detail?.prompt_role,
			};

			setComponent(responseFromApi?.responseBody?.component);
			setMessages((prev) => [...prev, responseObject]);
			setIsLoading(false);
			getAutoGeneratedChatName(id, prompt);
		} else {
			setIsLoading(false);
			handleToastChange(RESPONSE_FAILURE_MESSAGE, ERROR);
		}
	};

	const getAutoGeneratedChatName = async (id: string, searchTerm: string) => {
		if (chatTitle) {
			return;
		}
		const response: any = await getChatName(id, searchTerm);
		if (response.statusCode === OK) {
			setChatTitle(response.responseBody.name);
			setChatEditSearchVal(response.responseBody.name);
		} else {
			handleToastChange(CHAT_TITLE_FAILURE_MESSAGE, ERROR);
		}
	};

	const style = {
		'& .MuiOutlinedInput-root': {
			paddingTop: '0.2em',
			paddingBottom: '0.2em',
			'& fieldset': {
				border: `1px solid ${searchDisabled}`,
				borderRadius: '5px',
			},

			'&.Mui-focused fieldset': {
				border: `1px solid ${searchDisabled}`,
				borderRadius: '5px',
			},

			'&.MuiAccordionSummary-root:hover:not': {
				backgroundColor: textFieldColor,
			},
		},
		'& .MuiInputBase-input': {
			textOverflow: 'ellipsis',
			paddingRight: inputPadding,
		},
	};

	const newChatClicked = (isFromHistory: boolean) => {
		if (riskGPTKnowledgeOptions.length > 0) {
			dispatch(setRiskGptKnowledgeBase(riskGPTKnowledgeOptions[0].value));
		}
		dispatch(
			setRiskGptSessionDetails({ messages: [], sessionId: '', chatName: '' }),
		);
		dispatch(setFromHistory(false));
		setMessages([]);
		setSessionId('');
		setChatTitle('');
		setChatEditSearchVal('');
		setSearchVal('');
		setValue(isFromHistory ? CHAT_HISTORY : CHAT);
		dispatch(setActiveChatSessionUploadedFiles([]));
		// if click new chat, remove re-upload historyry
		dispatch(setActiveChatSessionUploadingFiles([]));
		dispatch(setReUploadHistory(null));
	};

	const onKeyDown = (e: any) => {
		e.stopPropagation();
		handleUploadViewMoreAction();
		if (e.key === 'Enter' && searchVal) {
			handleMessageSend();
		}
	};

	const handleEditTitleClicked = () => {
		setEditTitleClicked(true);
	};

	useEffect(() => {
		setEditTitleClicked(false);
	}, [value, messages.length === 0]);

	const handleToastClose = useCallback(() => {
		setIsToastOpen(false);
	}, []);

	const editChatTitleClicked = useCallback(async () => {
		const response: any = await updateChatName(sessionId, chatEditSearchVal);
		if (response.statusCode === OK) {
			setChatTitle(response.responseBody.name);
			setEditTitleClicked(false);
			handleToastChange(CHAT_NAME_UPDATE_SUCCESS_MESSAGE, SUCCESS);
		} else {
			handleToastChange(CHAT_NAME_UPDATE_FAILURE_MESSAGE, ERROR);
		}
	}, [sessionId, chatEditSearchVal]);

	const cancelClickedForChatTitle = () => {
		setEditTitleClicked(false);
		setChatEditSearchVal(chatTitle);
	};

	const titleTextFieldValueChanged = (e: ChangeEvent<HTMLInputElement>) => {
		setChatEditSearchVal(e.target.value);
	};

	const titleTextFieldKeyDown = (e: any) => {
		e.stopPropagation();
	};

	const fetchGPTMessagesResponse = async (
		id: string,
		isFetchFirstTime?: boolean,
	) => {
		const responseFromApi = await getGPTMessagesResponse(id);
		if (responseFromApi?.responseBody && responseFromApi?.statusCode === OK) {
			const resultArr = responseFromApi.responseBody;

			const filteredData =
				resultArr?.length > 0 ? filterDocsFromUploadedMessages(resultArr) : [];
			let formattedMessage = formatMessageFromApi(filteredData, loggedInUser);
			// if come back to last session, and has upload failed file add re-upload history to message
			if (
				sessionId === id &&
				reUploadHistory &&
				reUploadHistory?.uploadFailedFiles.length > 0
			) {
				formattedMessage = getMessageWithReUpload(
					formattedMessage,
					reUploadHistory,
					loggedInUser,
				);
			} else {
				// if go to another session, remove re-upload history
				dispatch(setActiveChatSessionUploadingFiles([]));
				dispatch(setReUploadHistory(null));
			}
			setMessages(formattedMessage);
			getSessionDetails(id, false, false, Boolean(isFetchFirstTime));
			setIsLoading(false);
		} else {
			setIsLoading(false);
			handleToastChange(CHAT_HISTORY_RETRIVE_FAILURE_MESSAGE, ERROR);
		}
	};

	const handleSelectChatHistory = async (id: string, knowledgeBase: string) => {
		newChatClicked(false);
		dispatch(setFromHistory(true));
		dispatch(setRiskGptKnowledgeBase(knowledgeBase));
		setIsLoading(true);
		setSessionId(id);
		// 2. fetch chat data
		fetchGPTMessagesResponse(id, true);
	};

	const reloadGPTMessages = () => {
		if (sessionId) {
			fetchGPTMessagesResponse(sessionId);
		}
	};

	const onFileUploadSuccess = async () => {
		// call the list api to fetch all uploaded documents
		getSessionDetails(sessionId, true, true, true);
		// delete re-upload history from message
		setMessages((prev) =>
			prev.filter(
				(message: IGPTMessageProps) => Boolean(message.isReUpload) === false,
			),
		);
		// clear uploading file
		dispatch(setActiveChatSessionUploadingFiles([]));
		// clear re-upload history
		dispatch(setReUploadHistory(null));

		const newMessage: IGPTMessageProps[] = getMessageWithUploadedFiles(
			messages,
			loggedInUser,
		);
		setMessages(newMessage);
	};

	const onFileUploadFailed = async (uploadResponse: any[]) => {
		// call the list api to fetch all uploaded documents
		const isGetSession = await getSessionDetails(sessionId, true);

		// save success and failed upload files to redux
		let failedFileArr: IUploadingFile[] = [...activeChatSessionUploadingFiles];

		const successFileArr: IFileInfo[] = [];
		uploadResponse.forEach((res: IUploadResponse) => {
			if (res.status === OK) {
				const fileInfo: IFileInfo = {
					name: res.data.detail[0].name,
					size: res.data.detail[0].size,
					id: res.data.detail[0].id,
				};
				successFileArr.push(fileInfo);
			}
		});
		// delete success uploaded file from history
		successFileArr.forEach((successFile) => {
			failedFileArr = failedFileArr.filter(
				(failedFile) => successFile.name !== failedFile.name,
			);
		});
		// set failed uploaded file to uploading file
		dispatch(setActiveChatSessionUploadingFiles(failedFileArr));
		// set successful uploaded file as uploaded file if get session failed
		if (!isGetSession) {
			dispatch(
				setActiveChatSessionUploadedFiles([
					...activeChatSessionUploadedFiles,
					...successFileArr,
				]),
			);
		}
		// reset the progress of the failed file upload
		const updatedArray = fileUploadStartHandler(failedFileArr);
		// set re-upload history
		const newReUploadHistory: IUploadingFileStatus = {
			timeStamp: String(new Date()),
			uploadSuccessFiles: successFileArr,
			uploadFailedFiles: updatedArray,
			reuploadSuccessFiles: [],
		};
		dispatch(setReUploadHistory(newReUploadHistory));
		// add re-upload to message
		const newMessage = getMessageWithReUpload(
			messages,
			newReUploadHistory,
			loggedInUser,
		);
		setMessages(newMessage);
		setIsUploadSelected(false);
		dispatch(clearUploadedDocumentProgress());
		const errResponse = uploadResponse.filter((res) => res.status !== OK);

		if (errResponse.length > 0) {
			if (errResponse[0].data.response) {
				const errorMsg = errResponse[0]?.data.response.data.error;
				handleToastChange(
					errorMsg?.length > 100 ? BAD_REQUEST_MSG : errorMsg,
					ERROR,
				);
			} else {
				handleToastChange(NETWORK_ISSUE_MESSAGE, ERROR);
			}
		}
	};

	useEffect(() => {
		cursorSetterHandler('ask-risk-link-input', cursorPosition);
	}, [searchVal, cursorPosition, value]);

	useEffect(() => {
		listRef.current?.lastElementChild?.scrollIntoView();
	}, [messages]);

	useEffect(() => {
		// if there comes new re-upload, will update the message
		let newMessage = getMessageWithReUpload(
			messages,
			reUploadHistory,
			loggedInUser,
		);
		// if new re-upload is null, means uploaded successfull, will update the message with uploaded files
		if (!reUploadHistory && activeChatSessionUploadedFiles.length > 0)
			newMessage = getMessageWithUploadedFiles(newMessage, loggedInUser);
		setMessages(newMessage);
		// if has re-upload successful file
		if (reUploadHistory && reUploadHistory.reuploadSuccessFiles.length > 0) {
			// call the api to fetch all uploaded documents
			getSessionDetails(sessionId, true);
		}
	}, [reUploadHistory]);

	const textFieldStyle = {
		height: '1.25rem',
		width: '100%',
		backgroundColor: textFieldColor,
	};
	const getMessages = () => (
		<div>
			{messages.length > 0 &&
				messages.map((res) => (
					<RiskGptMessageView
						key={new Date().getUTCMilliseconds().toString(36).substring(2, 15)}
						type={res?.type}
						name={res?.name}
						date={res?.date}
						time={res?.time}
						uniqueId={res?.uniqueId || ''}
						isUploadStatus={res?.isUploadStatus}
						reloadMessages={reloadGPTMessages}
						feedback={res?.feedback}
						sources={res?.sources}
						userInitial={`${res?.name
							.split(' ')[0]
							.charAt(0)
							.toUpperCase()}${res?.name
							.split(' ')[1]
							.charAt(0)
							.toUpperCase()}`}
						message={res?.message}
						regenerateLastQueryResponse={() => regenerateLastQueryResponse(res)}
						copyToClipBoard={() => copyToClipBoard(res?.message)}
						isReUpload={res.isReUpload}
						uploadUrl={uploadUrl}
						sessionId={sessionId}
						promptRole={res?.promptRole}
						handleToastChange={handleToastChange}
					/>
				))}
		</div>
	);

	const getBotMessageLoading = () => (
		<Grid item display="flex">
			<Box className={styles['bot-avatar']}>
				<BotAvatar />
			</Box>
			<Typography className={styles['bot-loader-grid']}>
				<div>
					<DotsLoader />
				</div>
			</Typography>
		</Grid>
	);

	const getSessionIDInfo = async () => {
		const response: any = await createChatSession(
			CHAT_NAME,
			riskGptKnowledgeBase,
		);
		if (response?.statusCode === OK) {
			const baseUrl = `${REACT_APP_GEN_AI_SERVICE_URL}/chat/sessions/${response?.responseBody?.id}/documents`;
			setUploadUrl(baseUrl);
			setSessionId(response.responseBody.id);

			setIsUploadSelected(true);
		} else {
			handleToastChange(CHAT_SESSION_ERROR_MESSAGE, ERROR);
		}
	};
	const openFileUploadModal = async () => {
		dispatch(setActiveChatSearchTextSelected(false));

		if (sessionId) {
			const baseUrl = `${REACT_APP_GEN_AI_SERVICE_URL}/chat/sessions/${sessionId}/documents`;
			setUploadUrl(baseUrl);
			setIsUploadSelected(true);
		} else {
			getSessionIDInfo();
		}
	};
	const onUploadCancel = () => {
		setIsUploadSelected(!isUploadSelected);
	};
	const handleUploadViewMoreAction = () => {
		dispatch(setActiveChatSearchTextSelected(true));
	};

	const onDeleteAction = (data: any) => {
		setDeleteFileInfo(data);
		setFileDeletionStatus(true);
	};

	const onDeleteUploadDoc = async (data: any) => {
		setDeleteBtnLoading(true);
		const payloadObj: IDeleteDocumentRequestParams = {
			sessionId,
			id: data?.id,
		};
		const response = await deleteChatSessionDocument(payloadObj);
		if (response?.statusCode === OK) {
			getSessionDetails(sessionId, true, false, true);
			handleToastChange(DELETE_FILE_SUCCESS_MESSAGE, SUCCESS);
		} else {
			handleToastChange(
				OPEN_RISK_GPT.CHAT_SESSION_ERROR_MESSAGE,
				TOAST_SEVERITY.ERROR,
			);
			setDeleteBtnLoading(false);
			setFileDeletionStatus(false);
		}
		fetchGPTMessagesResponse(sessionId);
	};

	const getMaxFileLimit = () => {
		const existingFiles = activeChatSessionUploadedFiles?.length ?? 0;
		const uploadingFiles = reUploadHistory?.uploadFailedFiles.length ?? 0;
		return maxFileAllowedForActiveChatSession - existingFiles - uploadingFiles;
	};

	const getExpandedStyle = (value: string) =>
		value === RECORDS
			? styles['chat-message-record-expanded']
			: styles['chat-message-area-expanded'];
	return (
		<Grid
			container
			className={styles['grid-container']}
			data-testid="riskGpt-container"
		>
			<Grid container className={styles['risk-gpt-icon-grid']}>
				<KnowledgeBase
					tabValue={value}
					disabled={messages.length > 0}
					sessionId={sessionId}
					messagesCount={messages.length}
				/>
				<Grid item display="flex" alignContent="center">
					<ButtonComp
						btnType="secondary"
						compClass={styles['new-chat-button']}
						startIcon={
							<Box className={styles['new-chat-button-icon']}>
								<Plus />
							</Box>
						}
						clickHandler={() => newChatClicked(false)}
						data-testid="new-chat"
					>
						{toSentenceCase(NEW_CHAT)}
					</ButtonComp>
					<Box className={styles['divider-line']}>
						<Divider />
					</Box>
					<IconButton
						className={styles['expand-icon']}
						onClick={shouldExpandRiskGptWindow}
						data-testid="expand-collapse"
						disabled={value === RECORDS}
					>
						{riskGptShouldExpand ? <Collapse /> : <Expand />}
					</IconButton>
					<IconButton
						className={styles['close-icon']}
						onClick={drawerCloseHandler}
						data-testid="close-window"
					>
						<DrawerClose fill={closeIconColor} />
					</IconButton>
				</Grid>
			</Grid>
			<Grid container>
				<TabContext value={value}>
					<Box className={styles['tab-box']}>
						<TabList
							onChange={(event: React.SyntheticEvent, value: string) =>
								handleTabChange(event, value)
							}
							data-testid="tab-list-askrisk"
							aria-label="chat tabs"
							TabIndicatorProps={{ sx: { bgcolor: selectedTabColor } }}
						>
							<Tab
								label={CHAT}
								sx={{ '&.Mui-selected': { color: selectedTabColor } }}
								className={styles['tab-label']}
								value={CHAT}
							/>
							<Tab
								label={PROMPT_LIBRARY}
								sx={{ '&.Mui-selected': { color: selectedTabColor } }}
								className={styles['tab-label']}
								value={PROMPT_LIBRARY}
							/>
							<Tab
								label={toSentenceCase(CHAT_HISTORY)}
								sx={{ '&.Mui-selected': { color: selectedTabColor } }}
								className={styles['tab-label']}
								value={CHAT_HISTORY}
							/>
							<Tab
								label={toSentenceCase(RECORDS)}
								sx={{ '&.Mui-selected': { color: selectedTabColor } }}
								className={styles['tab-label']}
								value={RECORDS}
							/>
						</TabList>
					</Box>
				</TabContext>
			</Grid>
			{chatTitle && value === CHAT && (
				<Grid
					container
					className={
						riskGptShouldExpand
							? styles['chat-name-grid-expanded']
							: styles['chat-name-grid']
					}
				>
					{!editTitleClicked && (
						<Grid
							item
							xs={12}
							lg={12}
							md={12}
							display="flex"
							flexDirection="row"
						>
							<Grid item xs={11} lg={11}>
								<Typography className={styles['chat-name']}>
									<Tooltip title={chatTitle} placement="bottom-start">
										<div className={styles['chat-name']}>{chatTitle}</div>
									</Tooltip>
								</Typography>
							</Grid>
							<Grid
								item
								xs={1}
								lg={1}
								alignItems="flex-end"
								className={styles['chat-edit-icon']}
								onClick={handleEditTitleClicked}
								data-testid="chat-title-edit"
							>
								<Pencil />
							</Grid>
						</Grid>
					)}
					{editTitleClicked && (
						<Grid
							item
							display="flex"
							width="100%"
							alignItems="center"
							gap="1rem"
						>
							<TextField
								size="small"
								variant="outlined"
								className={styles['title-update-textfield']}
								fullWidth
								value={chatEditSearchVal.trimStart()}
								onChange={titleTextFieldValueChanged}
								onKeyDown={titleTextFieldKeyDown}
								sx={style}
								InputProps={{
									inputProps: { 'data-testid': 'text-edit-text-field' },
									style: textFieldStyle,
								}}
							/>
							<Box
								onClick={editChatTitleClicked}
								data-testid="right-mark"
								className={
									chatEditSearchVal.trim() === ''
										? styles['right-mark-disable']
										: styles['right-mark']
								}
							>
								<Right fill={closeIconColor} />
							</Box>
							<Box
								onClick={cancelClickedForChatTitle}
								data-testid="cancel"
								className={styles['cancel-mark']}
							>
								<Cancel fill={closeIconColor} />
							</Box>
						</Grid>
					)}
				</Grid>
			)}
			<Grid
				container
				className={
					riskGptShouldExpand
						? getExpandedStyle(value)
						: styles['chat-message-area']
				}
			>
				{value === CHAT && (
					<div className={styles['grid-upper']}>
						<div className={styles['div-upper']} ref={listRef}>
							{messages.length === 0 && !loading && (
								<div>
									<Typography className={styles['start-here-text']}>
										{WELCOME_MESSAGE}
									</Typography>
								</div>
							)}
							{getMessages()}
							{loading && getBotMessageLoading()}
							<div ref={listRef} />
						</div>
					</div>
				)}
				{value === CHAT_HISTORY && (
					<ChatHistory
						handleSelectChatHistory={handleSelectChatHistory}
						handleToastChange={handleToastChange}
						handleChatHistoryDeleted={(isHistoryDeleted: boolean) =>
							newChatClicked(isHistoryDeleted)
						}
					/>
				)}
				{value === PROMPT_LIBRARY && (
					<PromptLibrary
						setSearchVal={onPromptSelect}
						setCursorPosition={setCursorPosition}
					/>
				)}
				{value === RECORDS && (
					<ChatRecords handleToastChange={handleToastChange} />
				)}
			</Grid>
			{(value === CHAT || value === PROMPT_LIBRARY) && (
				<>
					<Grid
						container
						className={
							riskGptShouldExpand
								? styles['chat-upload-grid-expanded']
								: styles['chat-upload-grid']
						}
					>
						{value === CHAT && (
							<UploadDocumentList
								listData={activeChatSessionUploadedFiles}
								onDeleteListHandler={onDeleteAction}
								showMore={activeChatSearchTextSelected}
								messages={messages}
							/>
						)}
						<TextField
							className={styles['text-field']}
							placeholder="Ask Risk Link"
							multiline
							maxRows={6}
							fullWidth
							value={searchVal}
							onChange={handleSearchChange}
							onKeyDown={onKeyDown}
							InputProps={{
								inputProps: { 'data-testid': 'search-field' },
								endAdornment: (
									<span className={styles['search-icon-container']}>
										{isAskRiskLinkUploadFileEnabled && (
											<ButtonComp
												btnType="secondary"
												disabled={getMaxFileLimit() <= 0}
												compClass={styles['upload-button']}
												clickHandler={openFileUploadModal}
												data-testid="riskgpt-upload-icon"
											>
												<Box className={styles['upload-button-icon']}>
													<PaperClip fill={paperClipIconColor} />
												</Box>
											</ButtonComp>
										)}

										<ButtonComp
											btnType="secondary"
											disabled={searchVal === '' || loading}
											compClass={styles['send-button']}
											clickHandler={handleMessageSend}
											data-testid="send-button"
										>
											<Box className={styles['send-button-icon']}>
												{searchVal === '' || loading ? (
													<SendDisable />
												) : (
													<Send />
												)}
											</Box>
										</ButtonComp>
									</span>
								),
							}}
							id="ask-risk-link-input"
							sx={style}
						/>
					</Grid>
					<Typography
						className={
							riskGptShouldExpand
								? styles['chat-disclaimer-text-expanded']
								: styles['disclaimer-text']
						}
					>
						{DISCLAIMER}
					</Typography>
				</>
			)}
			{isUploadSelected && (
				<FileUpload
					acceptTypes={ACCEPTED_CHAT_FILE_TYPES}
					isMultiFileUpload
					maxFileUploadLimit={getMaxFileLimit()}
					onCancelHandler={onUploadCancel}
					url={uploadUrl}
					onFileUploadSuccess={onFileUploadSuccess}
					onFileUploadFailed={onFileUploadFailed}
				/>
			)}
			{isFileDeleteSelected && (
				<DeleteFileModal
					data={deleteFileInfo}
					deleteActionHandler={onDeleteUploadDoc}
					handleModalClose={() => {
						setFileDeletionStatus(false);
						setDeleteFileInfo({});
					}}
					isOpen={isFileDeleteSelected}
					isLoading={deleteBtnLoading}
				/>
			)}
			<AlertComponent
				open={isToastOpen}
				toastMessage={toastMessage}
				severity={severity}
				handleCloseMessage={handleToastClose}
				vertical="top"
				horizontal="right"
			/>
		</Grid>
	);
};

export default OpenRiskGPT;
